// 副資材スコープ(小物札121/ブランドネーム122/その他ネーム123/その他副資材124)の表示行生成。
// 加工指示入力の fixedVerticalDisplayService / komonoFudaDisplayService の表示仕様を
// 品番マスタへ移植したもの(加工指示では 114-117 が 113 の代表行を縦持ち展開して表示する)。
// 表示元は副資材ベース行(120 = 加工指示113のD_SSDZ行)。サーバ(EnsureFukushizaiRows)が
// 全コード列に _cd/_nm を付与済みのため、名称解決は行内の _nm 読み出しで完結する。
export function createFukushizaiScopeDisplayService(deps) {
    const {
        DicVal,
        isEmpty,
        fnc,
        corpkey,
        account,
        entrydataRef,
        GridData,
        setEntryGridRows,
        resolveKakousijiGridPanelByType,
        kakousijiGroupControlStateRef,
        komonoFudaLabelCacheRef,
        komonoFudaLabelLoadingRef,
        KAKOUSIJI_FUKUSHIZAI_BASE_PANEL_TYPE
    } = deps;

    const KOMONOFUDA_PANEL_TYPE = "121";
    const FUKUSHIZAI_SCOPE_DISPLAY_PANEL_TYPES = ["121", "122", "123", "124"];
    // 加工指示の FIXED_VERTICAL_DISPLAY_CONFIGS(115-117)と同じ構成(typeのみ+7)。
    const FIXED_VERTICAL_CONFIGS = {
        "122": {
            visibleCodeId: "BRANDNAME_CD_DMY",
            sourceColumnIds: ["SSDZ_BRANDNAMECD1", "SSDZ_BRANDNAMECD2", "SSDZ_BRANDNAMECD3"]
        },
        "123": {
            visibleCodeId: "SONOTANAME_CD_DMY",
            sourceColumnIds: [
                "SSDZ_SONOTANAMECD1", "SSDZ_SONOTANAMECD2", "SSDZ_SONOTANAMECD3",
                "SSDZ_SONOTANAMECD4", "SSDZ_SONOTANAMECD5", "SSDZ_SONOTANAMECD6"
            ]
        },
        "124": {
            visibleCodeId: "SONOTAFUZOKU_CD_DMY",
            sourceColumnIds: [
                "SSDZ_SONOTAFUZOKUCD1", "SSDZ_SONOTAFUZOKUCD2", "SSDZ_SONOTAFUZOKUCD3",
                "SSDZ_SONOTAFUZOKUCD4", "SSDZ_SONOTAFUZOKUCD5"
            ]
        }
    };
    const KOMONOFUDA_SIZE_RATE_FIELD_IDS = [
        "SSDZ_KOMONOSIZERITU1", "SSDZ_KOMONOSIZERITU2", "SSDZ_KOMONOSIZERITU3",
        "SSDZ_KOMONOSIZERITU4", "SSDZ_KOMONOSIZERITU5", "SSDZ_KOMONOSIZERITU6"
    ];
    const komonoFudaLabelsUrl = "kakousiji/GetKomonoFudaLabels";

    function normalizeText(value) {
        return String(value ?? "").trim();
    }
    // "code:name" 表示値からコード部を取り出す。
    function extractCode(value) {
        const text = normalizeText(value);
        if (text === "") return "";
        return text.split(":")[0].trim();
    }
    // 桁揃え違い('1'と'01')を同一視するための比較キー。
    function compareKey(value) {
        const text = extractCode(value);
        if (text === "") return "";
        return /^\d+$/.test(text) ? String(Number(text)) : text;
    }
    function readRowCode(row, fieldId) {
        if (isEmpty(row)) return "";
        return extractCode(DicVal(row, fieldId, DicVal(row, fieldId + "_cd", DicVal(row, fieldId + "_CD", ""))));
    }
    function readRowName(row, fieldId) {
        if (isEmpty(row)) return "";
        return normalizeText(DicVal(row, fieldId + "_nm", DicVal(row, fieldId + "_NM", "")));
    }
    // 加工指示の formatFukushizaiMasterDisplayValue と同じ形式(code:name)。
    function formatDisplayValue(code, name) {
        const targetCode = normalizeText(code);
        const targetName = normalizeText(name);
        if (targetCode === "") return "";
        return targetName === "" ? targetCode : `${targetCode}:${targetName}`;
    }

    // 選択中アイテム/グループ(必要ならカラー優先)の代表行を返す。
    function pickFukushizaiScopeRepresentativeRow(baseRows) {
        const list = Array.isArray(baseRows) ? baseRows : [];
        const ctx = kakousijiGroupControlStateRef.current || {};
        const itemKey = compareKey(DicVal(ctx, "itemCd", ""));
        const groupKey = compareKey(DicVal(ctx, "groupNo", ""));
        if (itemKey === "") return null;
        const matches = list.filter((row) => !isEmpty(row)
            && compareKey(DicVal(row, "SSDZ_ITMCITEMCD", "")) === itemKey
            && (groupKey === "" || compareKey(DicVal(row, "SSDZ_FUKUSIZAICOLORGRP", "")) === groupKey));
        if (matches.length === 0) return null;
        const colorKey = compareKey(DicVal(ctx, "colorCd", ""));
        if (colorKey !== "") {
            const colorHit = matches.find((row) => compareKey(DicVal(row, "SSDZ_COLORCD", "")) === colorKey);
            if (!isEmpty(colorHit)) return colorHit;
        }
        return matches[0];
    }

    // ブランドネーム/その他ネーム/その他副資材: 代表行のコードN列を縦持ち行へ展開する。
    // 代表行なしでも加工指示と同様に空スロット行を出す。
    function buildFixedVerticalScopeRows(panelType, representativeRow) {
        const config = FIXED_VERTICAL_CONFIGS[String(panelType ?? "").trim()];
        if (isEmpty(config)) return [];
        const base = isEmpty(representativeRow) ? {} : representativeRow;
        return config.sourceColumnIds.map((sourceColumnId, index) => {
            const row = { ...base };
            const codeValue = readRowCode(base, sourceColumnId);
            const nameValue = readRowName(base, sourceColumnId);
            row.rowid = String(index);
            row[sourceColumnId] = codeValue;
            row[config.visibleCodeId] = formatDisplayValue(codeValue, nameValue);
            return row;
        });
    }

    // ---- 小物札ラベル(kakousiji/GetKomonoFudaLabels)の取得とキャッシュ ----
    function readKomonoFudaBrandCode() {
        return normalizeText(DicVal(entrydataRef.current, "PDT_BRDCODE", DicVal(entrydataRef.current, "BRD_CODE", "")));
    }
    function buildKomonoFudaLabelCacheKey(code, brandCode = readKomonoFudaBrandCode()) {
        const fuzokuHinban = normalizeText(code);
        const brand = normalizeText(brandCode);
        return brand === "" ? fuzokuHinban : `${fuzokuHinban}|${brand}`;
    }
    function normalizeKomonoFudaLabelRows(labels) {
        const list = Array.isArray(labels) ? labels : [];
        return list
            .map((row, index) => ({
                index: Number(DicVal(row, "index", index + 1) || index + 1),
                name1: normalizeText(DicVal(row, "name1", DicVal(row, "name", DicVal(row, "label", ""))))
            }))
            .filter((row) => row.name1 !== "")
            .sort((a, b) => a.index - b.index);
    }
    function getKomonoFudaLabels(code, fallbackName) {
        const fuzokuHinban = normalizeText(code);
        if (fuzokuHinban === "") return [];
        const cacheKey = buildKomonoFudaLabelCacheKey(fuzokuHinban);
        const cached = DicVal(komonoFudaLabelCacheRef.current, cacheKey, null);
        if (!isEmpty(cached)) return Array.isArray(cached.labels) ? cached.labels : [];
        requestKomonoFudaLabels(fuzokuHinban, fallbackName);
        const fallback = normalizeText(fallbackName);
        return fallback === "" ? [] : [{ index: 1, name1: fallback }];
    }
    function requestKomonoFudaLabels(code, fallbackName) {
        const fuzokuHinban = normalizeText(code);
        if (fuzokuHinban === "") return;
        const cacheKey = buildKomonoFudaLabelCacheKey(fuzokuHinban);
        if (!isEmpty(DicVal(komonoFudaLabelCacheRef.current, cacheKey, null))) return;
        if (DicVal(komonoFudaLabelLoadingRef.current, cacheKey, false)) return;
        if (isEmpty(fnc.current) || typeof fnc.current.postaxios !== "function") return;
        komonoFudaLabelLoadingRef.current[cacheKey] = true;
        const storeResult = (labels) => {
            komonoFudaLabelCacheRef.current[cacheKey] = { labels: labels };
            komonoFudaLabelLoadingRef.current[cacheKey] = false;
            refreshKomonoFudaScopePanelOnly();
        };
        try {
            fnc.current.postaxios(komonoFudaLabelsUrl, { corpkey, account, fuzokuHinban, brandCode: readKomonoFudaBrandCode() }, {
                onSuccess: (data) => {
                    const labels = normalizeKomonoFudaLabelRows(DicVal(data, "labels", DicVal(data, "rows", [])));
                    const fallback = normalizeText(fallbackName);
                    storeResult(labels.length !== 0 || fallback === "" ? labels : [{ index: 1, name1: fallback }]);
                },
                onError: () => {
                    const fallback = normalizeText(fallbackName);
                    storeResult(fallback === "" ? [] : [{ index: 1, name1: fallback }]);
                }
            }, true);
        } catch {
            komonoFudaLabelLoadingRef.current[cacheKey] = false;
        }
    }

    // 小物札: ラベル行×サイズ率列で表示行を展開する(加工指示 buildKomonoFudaDisplayRows と同仕様)。
    function buildKomonoFudaScopeRows(representativeRow) {
        const base = isEmpty(representativeRow) ? {} : representativeRow;
        const codeValue = readRowCode(base, "SSDZ_KOMONOFUDACD");
        const nameValue = readRowName(base, "SSDZ_KOMONOFUDACD");
        const labels = codeValue === "" ? [] : getKomonoFudaLabels(codeValue, nameValue);
        const contentCount = KOMONOFUDA_SIZE_RATE_FIELD_IDS.reduce((max, fieldId, index) => {
            return normalizeText(DicVal(base, fieldId, "")) !== "" ? index + 1 : max;
        }, 0);
        const rowCount = Math.min(KOMONOFUDA_SIZE_RATE_FIELD_IDS.length, Math.max(labels.length, contentCount));
        if (rowCount <= 0) return [];
        return Array.from({ length: rowCount }, (_, index) => {
            const slot = index + 1;
            const labelRow = labels.find((row) => Number(row.index) === slot) || labels[index] || {};
            const row = { ...base };
            row.rowid = String(index);
            row.SSDZ_KOMONOFUDACD = normalizeText(DicVal(labelRow, "name1", ""));
            row.KOMONOFUDA_NAIYO_DMY = normalizeText(DicVal(base, KOMONOFUDA_SIZE_RATE_FIELD_IDS[index] || "", ""));
            return row;
        });
    }

    function readFukushizaiScopeBaseRows() {
        const basePanel = resolveKakousijiGridPanelByType(KAKOUSIJI_FUKUSHIZAI_BASE_PANEL_TYPE);
        if (isEmpty(basePanel)) return [];
        const rows = GridData(basePanel);
        return Array.isArray(rows) ? rows : [];
    }

    // 副資材スコープ4グリッドの表示行を現在の選択(アイテム/グループ)で組み立て直す。
    function applyFukushizaiScopeDisplayRows(baseRowsOverride = null) {
        const baseRows = Array.isArray(baseRowsOverride) ? baseRowsOverride : readFukushizaiScopeBaseRows();
        const representativeRow = pickFukushizaiScopeRepresentativeRow(baseRows);
        FUKUSHIZAI_SCOPE_DISPLAY_PANEL_TYPES.forEach((panelType) => {
            const panelInfo = resolveKakousijiGridPanelByType(panelType);
            if (isEmpty(panelInfo)) return;
            const rows = panelType === KOMONOFUDA_PANEL_TYPE
                ? buildKomonoFudaScopeRows(representativeRow)
                : buildFixedVerticalScopeRows(panelType, representativeRow);
            setEntryGridRows({ id: panelInfo.id, type: panelType }, rows);
        });
    }

    // ラベル応答到着時は小物札グリッドのみ再構築する。
    function refreshKomonoFudaScopePanelOnly() {
        const panelInfo = resolveKakousijiGridPanelByType(KOMONOFUDA_PANEL_TYPE);
        if (isEmpty(panelInfo)) return;
        const representativeRow = pickFukushizaiScopeRepresentativeRow(readFukushizaiScopeBaseRows());
        setEntryGridRows({ id: panelInfo.id, type: KOMONOFUDA_PANEL_TYPE }, buildKomonoFudaScopeRows(representativeRow));
    }

    return {
        applyFukushizaiScopeDisplayRows
    };
}
